import { Injectable } from '@angular/core';
import {
    JsonMaterialQuoteModel,
    LocalStorageCommonService,
    MaterialQuoteModel,
    MaterialQuoteModelFromJson,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
    NotificationService,
} from '@evorait/pace-core';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageSalesQuoteService {
    private readonly KEY_SALES_QUOTE = 'salesQuote';
    public constructor(
        private readonly notificationService: NotificationService,
        private readonly localStorageCommonService: LocalStorageCommonService,
    ) {}

    public saveSalesQuote(salesQuote: MaterialQuoteModel): void {
        if (!salesQuote.notificationModel) {
            throw new Error('Tried to save MaterialQuoteModel without notification!');
        }

        const filteredSalesQuote = this.filterSalesQuoteByNotificationIdentifier(
            salesQuote.notificationModel.identifier,
        );
        filteredSalesQuote.push(salesQuote);
        this.localStorageCommonService.storageSet(this.KEY_SALES_QUOTE, filteredSalesQuote);
    }

    public getSalesQuote(notificationIdentifier: NotificationIdentifierModel): MaterialQuoteModelFromJson | undefined {
        const allSalesQuotes =
            this.localStorageCommonService.storageGet<JsonMaterialQuoteModel[]>(this.KEY_SALES_QUOTE) || [];

        const jsonSalesQuote = allSalesQuotes.find((localSalesQuote) => {
            if (!localSalesQuote.notificationIdentifier) {
                return false;
            }

            return new NotificationIdentifierModelFromJson(localSalesQuote.notificationIdentifier).equalsTo(
                notificationIdentifier,
            );
        });

        if (jsonSalesQuote) {
            return new MaterialQuoteModelFromJson(jsonSalesQuote);
        }

        return undefined;
    }

    public removeSalesQuoteFromLocalStorage(notificationIdentifier: NotificationIdentifierModel): void {
        const filteredSalesQuote = this.filterSalesQuoteByNotificationIdentifier(notificationIdentifier);
        this.localStorageCommonService.storageSet(this.KEY_SALES_QUOTE, filteredSalesQuote);
    }

    /**
     * This function will return all the stored sales quote data
     * excluding the sales quote for the given notification identifier.
     * It will also exclude any sales quote data stored for notifications no longer present in current NDP.
     */

    private filterSalesQuoteByNotificationIdentifier(
        notificationIdentifier: NotificationIdentifierModel,
    ): MaterialQuoteModel[] {
        const jsonSalesQuotesArray =
            this.localStorageCommonService.storageGet<JsonMaterialQuoteModel[]>(this.KEY_SALES_QUOTE) || [];
        const notificationsArray = this.notificationService.getAllNotifications();
        return jsonSalesQuotesArray
            .filter((jsonSalesQuote) => {
                if (!jsonSalesQuote.notificationIdentifier) {
                    return false;
                }

                const localNotificationIdentifier = new NotificationIdentifierModelFromJson(
                    jsonSalesQuote.notificationIdentifier,
                );
                const isNotificationValid =
                    notificationsArray.findIndex((notification) =>
                        notification.identifier.equalsTo(localNotificationIdentifier),
                    ) > -1;

                return isNotificationValid && !notificationIdentifier.equalsTo(localNotificationIdentifier);
            })
            .map((jsonSalesQuote) => new MaterialQuoteModelFromJson(jsonSalesQuote));
    }
}
